import { useEffect, useRef } from 'react';

const speakers = [
  {
    name: 'Dafne Borsatti',
    role: 'Convidada Central',
    titles: ['CEO, DB Project Design', 'Fundadora, Vertex Innovation & Design Hub'],
    bio: 'A única mulher à frente de um hub de arquitetura e construção nos EUA. Autora de "Um Passo de Fé". Apresentadora do podcast "The Vault". Mulheres que constroem, não apenas sonham.',
    quote: '"Construímos estruturas. Construímos empresas. Construímos legados."',
    image:
      'https://public.readdy.ai/ai/img_res/edited_2610928c38409cead64106a9d7f3e47c_2a3849d7.jpg',
    objectPosition: '50% 20%',
  },
  {
    name: 'Lizandra Arita',
    role: 'Palestrante',
    titles: ['Psicóloga & Engenheira', 'Cofundadora do Método Arita'],
    bio: 'Psicóloga, engenheira e uma das maiores referências em inteligência emocional e reprogramação do inconsciente no Brasil. Especialista em PNL, Hipnose, Gerenciamento de Emoções e Conflitos, cofundou o Instituto Arita ao lado do pai, Yasushi Arita, que desde 1990 já impactou mais de 100 mil pessoas.',
    quote: '"Reprogramar o inconsciente é o primeiro passo para soltar o rugido que estava preso dentro de você."',
    image: 'https://public.readdy.ai/ai/img_res/edited_8d3d5c5f6dc22d81cbb61ea1c3346824_0308f555.jpg',
    objectPosition: '50% 20%',
  },
  {
    name: 'Nikole Fernan',
    role: 'Produtora Executiva & Idealizadora',
    titles: ['Curadoria & Gestão de Produção', 'Criação de Experiências de Impacto'],
    bio: 'Expertise em curadoria, gestão de produção e criação de ambientes que transformam encontros em marcos. Planejamento cirúrgico com sensibilidade estética — cada detalhe sustenta a narrativa de força, elegância e resultado.',
    quote: '"Cada detalhe é uma declaração de intenção."',
    image:
      'https://storage.readdy-site.link/project_files/95ab1d24-f5e3-49af-acf5-f9d2f5689cc7/2c52b36f-3cdb-460a-a9f4-e68dd2c9d9f9_Screen-Shot-2026-03-26-at-11.27.26-AM.png?v=f84fb153ab47aa9e48d93fe1aa2d0447',
    objectPosition: '50% 50%',
  },
];

export default function BehindSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-target').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="bg-obsidian py-28 md:py-36 px-6 relative overflow-hidden"
    >
      {/* Top gold line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gold/30" />

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="animate-target opacity-0 transition-all duration-700 mb-3" style={{ transform: 'translateY(30px)' }}>
            <span className="font-sans text-xs tracking-widest3 uppercase text-gold" style={{ letterSpacing: '0.3em' }}>
              Presenças confirmadas
            </span>
          </div>
          <div className="animate-target opacity-0 transition-all duration-700" style={{ transform: 'translateY(30px)' }}>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-cream">
              As mentes que soltam{' '}
              <span className="italic text-gold">o rugido.</span>
            </h2>
          </div>
          <div className="animate-target opacity-0 transition-all duration-700 w-16 h-px bg-gold mx-auto mt-6" style={{ transform: 'translateY(30px)' }} />
        </div>

        {/* Speaker cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {speakers.map((speaker, index) => (
            <div
              key={speaker.name}
              className="animate-target opacity-0 transition-all duration-700 group"
              style={{ transform: 'translateY(30px)', transitionDelay: `${index * 200}ms` }}
            >
              <div className="flex flex-col gap-5">
                {/* Image */}
                <div className="w-full h-64 overflow-hidden border border-gold/20 group-hover:border-gold/50 transition-colors duration-300">
                  <img
                    src={speaker.image}
                    alt={speaker.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    style={{ objectPosition: speaker.objectPosition }}
                  />
                </div>
                {/* Content */}
                <div className="flex-1">
                  <span className="font-sans text-xs tracking-widest uppercase text-gold block mb-2" style={{ letterSpacing: '0.2em' }}>
                    {speaker.role}
                  </span>
                  <h3 className="font-serif text-2xl font-bold text-cream mb-3">{speaker.name}</h3>
                  <div className="w-8 h-px bg-gold mb-4" />
                  {speaker.titles.map((title) => (
                    <p key={title} className="font-sans text-xs text-gold mb-1 font-light">{title}</p>
                  ))}
                  <p className="font-sans text-sm text-white/90 leading-relaxed font-light mt-4">
                    {speaker.bio}
                  </p>
                </div>
              </div>
              {/* Quote */}
              <div className="mt-5 border-l-2 border-gold pl-5">
                <p className="font-serif italic text-gold/80 text-sm">{speaker.quote}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom gold line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gold/30" />
    </section>
  );
}
